/* ControlBar.tsx — the fixed top control strip. */

import { useFeedback } from './store';
import { FeedbackFab } from './CapturePanel';
import type { SyncState } from './types';

const SYNC_META: Record<SyncState, { dot: string; label: string; short: string }> = {
  idle: { dot: 'rgba(255,255,255,0.3)', label: 'Sync idle', short: 'Cloud' },
  loading: { dot: '#C08A1E', label: 'Loading from cloud…', short: 'Cloud · loading' },
  syncing: { dot: '#C08A1E', label: 'Saving to cloud…', short: 'Cloud · saving' },
  synced: { dot: '#087A70', label: 'Synced · shared with all reviewers', short: 'Cloud · synced' },
  offline: { dot: '#C94A3E', label: 'Offline · local only · click to retry', short: 'Cloud · offline' },
};

function SyncIndicator() {
  const { syncState, lastSyncedAt, forceSyncNow, tickets } = useFeedback();
  const meta = SYNC_META[syncState];
  const ago = lastSyncedAt ? new Date(lastSyncedAt).toLocaleTimeString() : '';
  const title = `${meta.label}${ago ? ` · last ${ago}` : ''} · ${tickets.length} tickets`;
  const spinning = syncState === 'syncing' || syncState === 'loading';
  return (
    <button
      className="pt-btn ghost pt-sync"
      onClick={forceSyncNow}
      title={title}
      aria-label={meta.label}
      style={{ padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: meta.dot,
          boxShadow: syncState === 'synced' ? '0 0 0 3px rgba(43,191,179,0.18)' : 'none',
          animation: spinning ? 'pf-fade 0.9s infinite' : 'none',
        }}
      />
      <span className="pt-label" style={{ fontSize: 10.5, color: 'var(--p-chrome-text-2)' }}>{meta.short}</span>
    </button>
  );
}

export function ControlBar({ brand }: { brand: string }) {
  const {
    syncEnabled,
    commentMode,
    setCommentMode,
    autoCapture,
    setAutoCapture,
    screenshots,
    setScreenshots,
    screenshotUrl,
    tickets,
    dashOpen,
    setDashOpen,
  } = useFeedback();

  return (
    <div className="proto-topbar">
      <div className="pt-brand">
        <span className="seam" />
        <span>{brand}</span>
      </div>

      <FeedbackFab />

      <div className="pt-right">
        {syncEnabled && <SyncIndicator />}
        <button
          className={'pt-btn pt-comment' + (commentMode ? ' on' : ' ghost')}
          onClick={() => setCommentMode(!commentMode)}
          title="Comment mode (c) — click anywhere on the screen to drop a pin"
          aria-label="Toggle comment mode"
        >
          {commentMode ? '◉ ' : '✎ '}<span className="pt-label">{commentMode ? 'Commenting' : 'Comment'}</span>
        </button>
        <button
          className={'pt-btn pt-tickets' + (dashOpen ? ' on' : ' ghost')}
          onClick={() => setDashOpen(!dashOpen)}
          title="Toggle ticket dashboard (t)"
          aria-label="Toggle ticket dashboard"
        >
          <span className="pt-label">Tickets</span>
          {tickets.length > 0 && (
            <span style={{ fontSize: 10, opacity: 0.7, marginLeft: 4 }}>{tickets.length}</span>
          )}
        </button>
        <button
          className={'pt-btn pt-auto' + (autoCapture ? ' on' : ' ghost')}
          onClick={() => setAutoCapture(!autoCapture)}
          title="Auto-capture errors, rejections and dead clicks as tickets"
          aria-label="Toggle auto-capture"
          style={{ fontSize: 10.5 }}
        >
          <span className="pt-label">Auto-capture {autoCapture ? 'on' : 'off'}</span>
        </button>
        {screenshotUrl && (
          <button
            className={'pt-btn pt-screenshot' + (screenshots ? ' on' : ' ghost')}
            onClick={() => setScreenshots(!screenshots)}
            title="Attach a screenshot of the current screen to new tickets."
            aria-label="Toggle screenshots"
            style={{ fontSize: 10.5 }}
          >
            <span className="pt-label">Screenshot {screenshots ? 'on' : 'off'}</span>
          </button>
        )}
      </div>
    </div>
  );
}
